import React, {useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import {Horizontal, PageIcon, PopOut, ThemeName, Vertical} from '@taopix/taopix-design-system';
import AutoLayoutOptionButtons from './Actions/AutoLayoutOptionButtons';
import useShuffleLayoutButtons from '../../Hooks/useShuffleLayoutButtons';
import {PageDetectionActionType} from '@taopix/taopix-core';
import {ShuffleLayoutArgs} from '@taopix/taopix-core/dist/types/types';
import {useTaopix} from '../Context/TaopixContext';
import {onSetLayout} from '../../Utils/Layout';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import PageActionButtons from './PageActionButtons';
import {usePageActions} from '../Context/PageActions/PageActionsContext';

const PageActionsPalette = () => {
  const app = useTaopix();
  const ui = useUiState();
  const buttons = usePageActions();
  const [buttonState, , api] = useShuffleLayoutButtons();
  const [alert, setAlert] = useState('');
  const {t} = useTranslation(['*']);

  const availableButtons = useMemo(() => {
    if (!ui.state.isSmallScreen) {
      return buttons;
    }
    const idx = buttons.findIndex(btn => 'showMore' === btn.name);
    return buttons.slice(-1 === idx ? 0 : idx + 1, buttons.length);
  }, [ui.state.isSmallScreen, buttons]);

  useEffect(() => {
    // close the alert when the page changes
    setAlert('');
  }, [ui.state.pageNumber]);

  useEffect(() => {
    // if no buttons are enabled, close the auto layout buttons sub palette
    if (
      Object.values(buttonState).filter(truthy => truthy).length < 2 &&
      ui.state.isSmallScreen &&
      'pageActionsPalette/autoLayoutOptionsPalette' === ui.state.palette.section
    ) {
      ui.dispatch(UserInterfaceActions.setToolPaletteSection('pageActionsPalette'));
    }
  }, [buttonState]);

  // Close the palette if we switch screen size
  useEffect(() => {
    if (!ui.state.isSmallScreen && ui.state.palette.section?.includes('pageActionsPalette')) {
      ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
    }
  }, [ui.state.isSmallScreen]);

  useEffect(() => {
    if (alert.length > 0) {
      setTimeout(() => {
        onCloseAlert();
      }, 3000);
    }
  }, [alert]);

  const onRequestLayout = useCallback(
    (payload: ShuffleLayoutArgs, detectionArea: PageDetectionActionType) => {
      onSetLayout(app, payload, detectionArea, api, setAlert);
    },
    [api]
  );

  const onCloseAlert = () => {
    setAlert(current => '');
  };

  const warningPopOutProps: Partial<PopOutProps> = {
    theme: 'tpx-theme-toolpaletteContent',
    themeTip: 'Sidebar (Content)',
    contentTheme: ThemeName.Warning,
    arrow: true,
    anchorId: 'autoLayoutOptionsPalette',
    className: 'flex flex-col p-md space-y-sm max-w-[300rem]',
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    popDirection: 'up',
    offset: {x: 0, y: -10},
    onClose: () => onCloseAlert(),
    excludeIdFromClickOutside: ['autoLayoutOptionsPalette'],
  };

  const buttonSizingClasses = classNames(
    'px-xs',
    'shrink-0',
    ...(ui.state.isSmallScreen
      ? [
          // 'w-[calc(100vw/3.5)]',
          'min300:w-[calc(100vw/3.5)]',
          'min400:w-[calc(100vw/4.5)]',
          'min500:w-[calc(100vw/5.5)]',
          'min600:w-[calc(100vw/6.5)]',
          'min700:w-[calc(100vw/7.5)]',
          'min800:w-[calc(100vw/8.5)]',
          'min900:w-[calc(100vw/9.5)]',
          'min1000:w-[calc(100vw/10.5)]',
        ]
      : [''])
  );

  const pageOptionButtonProps: Partial<ButtonProps> = {
    buttonStyle: 'standard',
    boxless: ui.state.isSmallScreen ? true : false,
    stacked: ui.state.isSmallScreen ? true : false,
    size: ui.state.isSmallScreen ? 'medium' : 'small',
    labelAlignment: ui.state.isSmallScreen ? undefined : 'left',
    className: ui.state.isSmallScreen ? buttonSizingClasses : undefined,
    labelLines: ui.state.isSmallScreen ? 2 : 1,
    disabled: false,
  };

  return (
    <ToolPaletteSection
      id="pageActionsPalette"
      title={t('*:str_LabelPageOptions')}
      buttonIcon={<PageIcon />}
      sectionKey={'pageActionsPalette'}
      showButton={false}
    >
      <div className="flex overflow-auto pb-lg pt-sm">
        <PageActionButtons
          pageNumber={ui.state.pageNumber}
          buttonProps={pageOptionButtonProps}
          buttons={availableButtons}
        />
        <ToolPaletteSection
          id="autoLayoutOptionsPalette"
          title={t('str_LabelApplyNewLayout')}
          sectionKey={'autoLayoutOptionsPalette'}
          showButton={false}
          subSection={true}
        >
          <div className="flex justify-center overflow-auto pb-lg">
            <AutoLayoutOptionButtons
              buttonState={buttonState}
              api={api}
              onRequestLayout={onRequestLayout}
            />
          </div>
          <PopOut id={'noStylesAvailable'} open={alert.length > 0} {...warningPopOutProps}>
            {alert}
          </PopOut>
        </ToolPaletteSection>
      </div>
    </ToolPaletteSection>
  );
};

export default PageActionsPalette;
